import { ReactElement, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { micOutline } from "ionicons/icons";
import {
  DropdownQuestionType,
  CommonAnswersType,
} from "../../../../types/Question";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import { IonButton, IonIcon, IonItem, IonLabel } from "@ionic/react";

let globalIgnoreClick = false;

const DRAG_THRESHOLD = 5; // px distance to differentiate "click" vs. "drag"

interface AnswerDropdownProps {
  question: DropdownQuestionType;
  value?: string;
  onChange: (value: string) => void;
}

const AnswerDropdown = ({
  question,
  value = "",
  onChange,
}: AnswerDropdownProps): ReactElement => {
  const { t } = useTranslation();
  const {
    detail: { answers },
  } = question;

  const { startListnening, isListening } = useVocalisation(
    answers.map(({ label }: CommonAnswersType) => label),
    (label) => {
      if (label)
        onChange(String(answers.find((a) => a.label === label)?.id));
    }
  );

  const { selectedRole } = useConfig();
  const isSpeechRecEnabled =
    selectedRole?.attributes?.is_speech_recognition_enabled;

  // Modal state for our custom dropdown
  const [showModal, setShowModal] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Variables to handle drag scrolling
  let isDragging = false;
  let startY = 0;
  let startScrollTop = 0;
  let totalDelta = 0;

  useEffect(() => {
    // Disable custom drag logic on devices with a coarse pointer (touchscreens)
    if (window.matchMedia("(pointer: coarse)").matches) {
      return;
    }
    const el = modalContentRef.current;
    if (!el) return;

    const handlePointerDown = (e: PointerEvent) => {
      isDragging = true;
      startY = e.clientY;
      startScrollTop = el.scrollTop;
      totalDelta = 0;
      el.style.cursor = "grabbing";
      e.preventDefault(); // Prevent text selection
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const delta = e.clientY - startY;
      totalDelta = Math.abs(delta);
      el.scrollTop = startScrollTop - delta;
    };

    const handlePointerUp = () => {
      isDragging = false;
      el.style.cursor = "grab";
    };

    el.addEventListener("pointerdown", handlePointerDown);
    el.addEventListener("pointermove", handlePointerMove);
    el.addEventListener("pointerup", handlePointerUp);
    el.addEventListener("pointerleave", handlePointerUp);
    el.addEventListener("pointercancel", handlePointerUp);

    return () => {
      el.removeEventListener("pointerdown", handlePointerDown);
      el.removeEventListener("pointermove", handlePointerMove);
      el.removeEventListener("pointerup", handlePointerUp);
      el.removeEventListener("pointerleave", handlePointerUp);
      el.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [showModal]);

  // Handle tap vs. drag for selecting an answer
  const handleItemClick = (optionId: number) => {
    if (totalDelta > DRAG_THRESHOLD) {
      return;
    }
    onChange(String(optionId));
    setShowModal(false);
    globalIgnoreClick = true;
    setTimeout(() => {
      globalIgnoreClick = false;
    }, 300);
  };

  // Find the selected answer's label for display
  const selectedAnswerLabel = value
    ? answers.find((a) => String(a.id) === value)?.label
    : null;

  // Determine if the device is a touch device (coarse pointer)
  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  return (
    <>
      <StyledTextField
        onClick={() => {
          if (!globalIgnoreClick) {
            setShowModal(true);
          }
        }}
      >
        <Field>
          <SelectedValue>
            {selectedAnswerLabel
              ? selectedAnswerLabel
              : t("answer_dropdwon_placeholder")}
          </SelectedValue>
        </Field>
      </StyledTextField>

      {isSpeechRecEnabled && (
        <IonItem>
          <IonLabel>
            <p>{t("common_click_to_speak")}</p>
          </IonLabel>
          <IonButton onClick={startListnening} slot="end" fill="clear">
            {isListening ? (
              <IonIcon slot="icon-only" src="/assets/icon/loading.svg" />
            ) : (
              <IonIcon slot="icon-only" icon={micOutline} />
            )}
          </IonButton>
        </IonItem>
      )}

      {showModal &&
        createPortal(
          <ModalBackdrop onClick={() => setShowModal(false)}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <ModalWrapper>
                <ModalHeader>{t("common_dropdown_title")}</ModalHeader>
                <ModalContent ref={modalContentRef}>
                  {answers.map((option: CommonAnswersType) => (
                    <ListItemWrapper
                      key={option.id}
                      {...(isTouchDevice
                        ? { onClick: () => handleItemClick(option.id) }
                        : {
                            onPointerUp: (e) => {
                              e.stopPropagation();
                              handleItemClick(option.id);
                            },
                          })}
                    >
                      {option.label}
                    </ListItemWrapper>
                  ))}
                </ModalContent>
                <StyledCloseButton onClick={() => setShowModal(false)}>
                  {t("common_dropdown_close_button")}
                </StyledCloseButton>
              </ModalWrapper>
            </ModalContainer>
          </ModalBackdrop>,
          document.body
        )}
    </>
  );
};

export default AnswerDropdown;
const StyledTextField = styled.div`
  display: flex;
  align-items: center;
  background: white;
  padding: 14px 10px;
  border-bottom: 1px solid #444444;
  font-size: 19px;
  color: #444;
  cursor: pointer;
`;

const Field = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const SelectedValue = styled.div`
  font-size: 16px;
  color: #444;
  padding: 5px 10px;
`;

const SpeechRecContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f0f0f0;
`;

const SpeechRecLabel = styled.div`
  font-size: 16px;
  flex: 1;
  color: #444;
`;

const SpeechRecButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
`;

const LoadingIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const MicIcon = styled.img`
  width: 24px;
  height: 24px;
`;

/* Modal Components */

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000; /* High z-index to cover the Ionic header */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: min(600px, 80%);
`;

const ModalWrapper = styled.div`
  background-color: white;
  border-radius: 18px;
  width: 100%;
  min-height: 300px;
  max-height: 80vh;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 18px 50px;
  font-size: 18px;
  font-weight: bold;
  color: #444;
  background: #f0f0f0;
  border-bottom: 1px solid #ccc;
  border-radius: 18px 18px 0 0;
`;

const ModalContent = styled.div`
  flex-grow: 1;
  background: white;
  overflow-y: auto;
  cursor: grab;
  max-height: calc(80vh - 140px); /* adjust for header and close button */
  border-radius: 4px;
  margin: 0px 0px 5px 0px;
  padding: 0px 20px;
`;

const StyledCloseButton = styled.button`
  font-size: 18px;
  font-weight: bold;
  margin: 0 20px 20px;
  padding: 0 20px;
  height: 40px;
  background: #F0F0F0;
  border: none;
  border-radius: 18px;
  color: black;
  text-transform: none;
  font-family: "Ciutadella Rounded", sans-serif;
  cursor: pointer;
`;

const ListItemWrapper = styled.div`
  padding: 16px;
  border-bottom: 1px solid #ccc;
  &:active {
    background-color: #ddd;
  }
  &:hover {
    background-color: #f7f7f7;
  }
`;
